import React from 'react'
import { C } from '../styles/tokens'
import { StepCounter } from '../native/stepCounter'

const GOAL = 8000

// Capacitor + Health Connect (fallback) are only meaningful inside the native app.
let Capacitor = null, HealthConnect = null
try { Capacitor = require('@capacitor/core').Capacitor } catch (e) {}
try { HealthConnect = require('capacitor-health-connect').HealthConnect } catch (e) {}

const isNative = () => !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform())

export default function StepsCard() {
  // init | web | needperm | ready | unsupported | notinstalled | error
  const [state, setState] = React.useState('init')
  const [steps, setSteps] = React.useState(null)
  const [history, setHistory] = React.useState([]) // [{date, steps}] last 7 days
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState('')
  const [live, setLive] = React.useState(false) // true when using the real-time hardware sensor
  const listenerRef = React.useRef(null)

  const loadHistory = React.useCallback(async () => {
    try { const { history } = await StepCounter.getHistory({ days: 7 }); setHistory(history || []) } catch (e) {}
  }, [])

  // ---- Direct hardware sensor path (preferred: near real-time, brand-independent) ----
  const startSensor = React.useCallback(async () => {
    setBusy(true); setErr('')
    try {
      const perm = await StepCounter.requestPermissions()
      if (perm && perm.activity && perm.activity !== 'granted') {
        setErr('Motion/activity permission was not granted.'); setState('needperm'); setBusy(false); return
      }
      const res = await StepCounter.start()
      setSteps(res && typeof res.steps === 'number' ? res.steps : 0)
      // live updates as you walk
      if (!listenerRef.current) {
        listenerRef.current = await StepCounter.addListener('stepsChange', (d) => {
          if (d && typeof d.steps === 'number') setSteps(d.steps)
        })
      }
      setLive(true); setState('ready')
      loadHistory()
      // One-time: ask to exempt Revive from battery optimization so the
      // background step service keeps counting when the app is closed.
      try {
        if (!localStorage.getItem('revive_batt_asked')) {
          localStorage.setItem('revive_batt_asked', '1')
          StepCounter.requestBatteryExemption && StepCounter.requestBatteryExemption()
        }
      } catch (e) {}
    } catch (e) {
      setErr(e && e.message ? e.message : 'Could not start step tracking'); setState('error')
    } finally { setBusy(false) }
  }, [loadHistory])

  // ---- Health Connect fallback (used only if the phone has no step-counter sensor) ----
  const readHC = React.useCallback(async () => {
    setBusy(true); setErr('')
    try {
      const now = new Date()
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const { records } = await HealthConnect.readRecords({
        type: 'Steps',
        timeRangeFilter: { type: 'between', startTime: startOfDay, endTime: now },
      })
      setSteps((records || []).reduce((s, r) => s + (r.count || 0), 0))
      setState('ready')
    } catch (e) {
      setErr('Could not read steps. ' + (e && e.message ? e.message : '')); setState('error')
    } finally { setBusy(false) }
  }, [])

  const connectHC = React.useCallback(async () => {
    if (!HealthConnect) { setState('unsupported'); return }
    try {
      const { availability } = await HealthConnect.checkAvailability()
      if (availability === 'NotSupported') { setState('unsupported'); return }
      if (availability === 'NotInstalled') { setState('notinstalled'); return }
      const perm = await HealthConnect.checkHealthPermissions({ read: ['Steps'], write: [] })
      if (perm && perm.hasAllPermissions) { await readHC() } else { setState('needperm') }
    } catch (e) { setErr(e && e.message ? e.message : 'Health Connect error'); setState('error') }
  }, [readHC])

  const init = React.useCallback(async () => {
    if (!isNative()) { setState('web'); return }
    // Prefer the direct hardware sensor; only fall back to Health Connect if absent.
    try {
      if (StepCounter) {
        const { available } = await StepCounter.isAvailable()
        if (available) {
          const perm = await StepCounter.checkPermissions()
          if (perm && perm.activity === 'granted') { await startSensor() }
          else { setState('needperm') }
          return
        }
      }
    } catch (e) { /* fall through to Health Connect */ }
    await connectHC()
  }, [startSensor, connectHC])

  React.useEffect(() => {
    init()
    return () => { if (listenerRef.current) { try { listenerRef.current.remove() } catch (e) {} listenerRef.current = null } }
  }, [init])

  // "Connect" button: sensor path if available, otherwise Health Connect.
  const connect = async () => {
    if (live || (StepCounter && !HealthConnect)) return startSensor()
    setBusy(true); setErr('')
    try {
      if (StepCounter) {
        const { available } = await StepCounter.isAvailable()
        if (available) { setBusy(false); return startSensor() }
      }
      const res = await HealthConnect.requestHealthPermissions({ read: ['Steps'], write: [] })
      if (res && res.hasAllPermissions) await readHC()
      else { setErr('Step permission was not granted.'); setState('needperm') }
    } catch (e) { setErr(e && e.message ? e.message : 'Permission request failed'); setState('error') }
    finally { setBusy(false) }
  }

  const refresh = async () => {
    if (live) { try { const r = await StepCounter.getSteps(); if (r && typeof r.steps === 'number') setSteps(r.steps); loadHistory() } catch (e) {} }
    else { await readHC() }
  }

  const openHC = async () => { try { await HealthConnect.openHealthConnectSetting() } catch (e) {} }

  const pct = steps != null ? Math.min(100, Math.round((steps / GOAL) * 100)) : 0
  const R = 46, CIRC = 2 * Math.PI * R
  const dash = CIRC * (pct / 100)

  const wrap = (children) => (
    <div style={{ background: 'white', borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 2px 10px rgba(13,34,24,.05)', marginTop: '1.25rem' }}>
      <div style={{ background: C.headerGrad, padding: '.9rem 1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1.1rem' }}>🚶</span>
        <div>
          <div style={{ fontWeight: 700, color: 'white', fontSize: '.9rem' }}>Daily Steps</div>
          <div style={{ fontSize: '.66rem', color: 'rgba(255,255,255,.6)', marginTop: 1 }}>{live ? 'Live from your phone’s motion sensor' : 'Auto-tracked from Health Connect'}</div>
        </div>
      </div>
      <div style={{ padding: '1.25rem' }}>{children}</div>
    </div>
  )

  const btn = (label, onClick, primary = true) => (
    <button onClick={onClick} disabled={busy}
      style={{ width: '100%', padding: '.8rem', borderRadius: 10, border: primary ? 'none' : `1.5px solid ${C.g600}`, background: primary ? C.g700 : 'white', color: primary ? 'white' : C.g700, fontWeight: 700, fontSize: '.88rem', cursor: busy ? 'default' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
      {busy ? 'Please wait…' : label}
    </button>
  )

  const note = (t) => <div style={{ fontSize: '.8rem', color: C.muted, lineHeight: 1.6, marginBottom: '1rem' }}>{t}</div>

  if (state === 'init') return wrap(<div style={{ textAlign: 'center', color: C.muted, fontSize: '.85rem' }}>Loading…</div>)

  if (state === 'web') return wrap(
    note('📱 Step tracking runs in the Revive app (Android). Install/open the app to auto-track your daily steps.')
  )

  if (state === 'unsupported') return wrap(note('This phone doesn’t support automatic step tracking.'))

  if (state === 'notinstalled') return wrap(<>
    {note('Health Connect isn’t set up on this phone yet. Open it to supply step data.')}
    {btn('Open Health Connect', openHC)}
  </>)

  if (state === 'needperm') return wrap(<>
    {note('Allow Revive to count your steps using your phone’s motion sensor. Your data stays on your phone; we only show your daily total.')}
    {btn('Connect step tracking', connect)}
  </>)

  if (state === 'error') return wrap(<>
    <div style={{ fontSize: '.82rem', color: C.error, marginBottom: '1rem', lineHeight: 1.5 }}>{err || 'Something went wrong.'}</div>
    {btn('Try again', init)}
  </>)

  // ready
  return wrap(
    <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: 120, height: 120 }}>
        <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="60" cy="60" r={R} fill="none" stroke={C.g100} strokeWidth="10" />
          <circle cx="60" cy="60" r={R} fill="none" stroke={C.g500} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${dash} ${CIRC}`} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.5rem', color: C.g800 }}>{(steps ?? 0).toLocaleString()}</div>
          <div style={{ fontSize: '.6rem', color: C.muted }}>steps</div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: '.8rem', color: C.text, marginBottom: '.35rem' }}><strong>{pct}%</strong> of your {GOAL.toLocaleString()} goal</div>
        <div style={{ fontSize: '.72rem', color: C.muted, marginBottom: '.85rem' }}>{steps >= GOAL ? 'Goal reached — great for circulation & skin!' : `${(GOAL - (steps ?? 0)).toLocaleString()} to go`}</div>
        {live && <div style={{ fontSize: '.66rem', color: C.g600, marginBottom: '.6rem', display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: C.g500, display: 'inline-block' }} /> Live — walk and watch it update</div>}
        {btn('↻ Refresh', refresh, false)}
      </div>
      {history && history.length > 0 && <Last7Days history={history} goal={GOAL} />}
    </div>
  )
}

// Compact 7-day step calendar (daily numbers as bars).
function Last7Days({ history, goal }) {
  const max = Math.max(goal, ...history.map(h => h.steps || 0), 1)
  const dow = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
  return (
    <div style={{ width: '100%', marginTop: '.4rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: '.72rem', color: C.muted, marginBottom: '.6rem', fontWeight: 600 }}>Last 7 days</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6, height: 78 }}>
        {history.map((h, i) => {
          const d = new Date(h.date + 'T00:00:00')
          const isToday = i === history.length - 1
          const barH = Math.max(4, Math.round(((h.steps || 0) / max) * 56))
          const hit = (h.steps || 0) >= goal
          return (
            <div key={h.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ fontSize: '.55rem', color: C.muted }}>{(h.steps || 0) >= 1000 ? (h.steps / 1000).toFixed(1) + 'k' : (h.steps || 0)}</div>
              <div style={{ width: '100%', maxWidth: 20, height: barH, borderRadius: 5, background: hit ? C.g500 : (isToday ? C.g600 : C.g100), transition: 'height .3s' }} />
              <div style={{ fontSize: '.58rem', color: isToday ? C.g700 : C.muted, fontWeight: isToday ? 700 : 400 }}>{dow[d.getDay()]}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
